import type { Ref, SubmitEvent } from "react";

import { useTranslation } from "react-i18next";

import type { FriendGroup } from "@/api/bale.zod";

import { useCreateFriendGroup } from "@/api/bale";
import { ActionBox } from "@/components/ActionBox";
import { Button } from "@/components/Button";
import { CardSubtitleHeader, CardTextContent } from "@/components/Card";
import { Dialog } from "@/components/Dialog";
import { Form } from "@/components/Form";
import { FormField } from "@/components/FormField";
import { Input } from "@/components/Input";

export function CreateFriendGroupModal({
  ref,
  onClose,
}: {
  ref: Ref<HTMLDialogElement>;
  onClose?: (friendGroup?: FriendGroup) => void;
}) {
  const { t } = useTranslation();

  const createFriendGroup = useCreateFriendGroup({
    mutation: {
      onSuccess: (data) => onClose?.(data.data.friend_group),
    },
  });

  function submit(event: SubmitEvent<HTMLFormElement>) {
    const form = new FormData(event.currentTarget);
    const name = form.get("name")! as string;
    const description = form.get("description")! as string;
    createFriendGroup.mutate({ data: { name, description } });
  }

  return (
    <Dialog ref={ref} className="flex w-lg flex-col">
      <CardSubtitleHeader>{t("create-friend-group")}</CardSubtitleHeader>
      <CardTextContent>
        <Form error={createFriendGroup.error} className="flex grow flex-col gap-4" onSubmit={submit}>
          <FormField title={t("friend-group-name-title")}>
            <Input required type="text" name="name" placeholder={t("friend-group-name-placeholder")} />
          </FormField>
          <FormField title={t("friend-group-description-title")}>
            <Input type="text" name="description" placeholder={t("friend-group-description-placeholder")} />
          </FormField>
          <ActionBox>
            <Button type="button" name="action" attention="danger" onClick={() => onClose?.()}>
              {t("cancel")}
            </Button>
            <Button type="submit" attention="submit">
              {t("save")}
            </Button>
          </ActionBox>
        </Form>
      </CardTextContent>
    </Dialog>
  );
}
